import React, { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { AlertTriangle, ShieldCheck, Loader2, ExternalLink } from "lucide-react";
import { getDisplayMode, isRestrictedUpiIntentContext } from "@/lib/paymentContext";
import { waitForRadixReleased } from "@/lib/dialogRelease";

const API = process.env.REACT_APP_BACKEND_URL;
const CHECKOUT_JS = "https://checkout.razorpay.com/v1/checkout.js";
const PRICE_PER_PDF_RUPEES = 10;

const DOC_CATALOG = [
  { key: "vakalatnama", label: "Vakalatnama" },
  { key: "adjournment", label: "Adjournment Application" },
  { key: "addressMemo", label: "Address Memo" },
];

function loadCheckoutJs() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no-window"));
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${CHECKOUT_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => reject(new Error("checkout-load-failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = CHECKOUT_JS;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("checkout-load-failed"));
    document.head.appendChild(s);
  });
}

/**
 * PaymentDownloadDialog
 * Props:
 *   open, onOpenChange
 *   initialSelected: array of doc keys pre-checked (e.g. ['vakalatnama'] or all 3)
 *   onDownload(docKeys): async — called ONLY after successful payment verification.
 *                        Must generate and download the specified PDFs.
 *   userEmail, userName: prefill Razorpay Checkout
 */
export default function PaymentDownloadDialog({
  open,
  onOpenChange,
  initialSelected = [],
  onDownload,
  userEmail = "",
  userName = "",
}) {
  const [selected, setSelected] = useState(new Set(initialSelected));
  const [configured, setConfigured] = useState(null); // null=unknown, true/false
  const [pricePaise, setPricePaise] = useState(1000);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setSelected(new Set(initialSelected));
  }, [open, initialSelected]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/payments/config`, {
          method: "POST",
          credentials: "include",
        });
        if (!r.ok) {
          if (!cancelled) setConfigured(false);
          return;
        }
        const j = await r.json();
        if (cancelled) return;
        setConfigured(!!j.configured);
        if (j.price_per_pdf_paise) setPricePaise(j.price_per_pdf_paise);
      } catch {
        if (!cancelled) setConfigured(false);
      }
    })();
    // Warm up Razorpay checkout.js the moment the dialog opens so the
    // Pay click path is as short and synchronous as possible — this
    // preserves Android Chrome user-activation for the eventual UPI
    // Intent navigation. loadCheckoutJs() is idempotent + memoized on
    // window.Razorpay so this is safe to call multiple times.
    loadCheckoutJs().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const count = selected.size;
  const amountRupees = useMemo(() => (count * pricePaise) / 100, [count, pricePaise]);
  const clampedCount = Math.min(3, Math.max(0, count));

  const toggle = (k) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else if (next.size < 3) next.add(k);
      return next;
    });
  };

  const startPayment = async () => {
    if (clampedCount < 1) {
      toast.error("Please select at least one document.");
      return;
    }
    if (configured === false) {
      toast.error("Payment gateway is not yet configured. Please try again later.");
      return;
    }
    setBusy(true);
    try {
      // 1) Create order
      const orderRes = await fetch(`${API}/api/payments/create-order`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pdf_count: clampedCount }),
      });
      if (orderRes.status === 503) {
        toast.error("Payment temporarily unavailable. Please try again later.");
        return;
      }
      if (!orderRes.ok) throw new Error(`create-order-${orderRes.status}`);
      const order = await orderRes.json();

      // 2) Load Razorpay Checkout & open
      await loadCheckoutJs();

      // Deterministic handover to Razorpay Checkout for reliable Android
      // UPI Intent launch:
      //   (a) flushSync forces React to commit the Radix Dialog close
      //       synchronously — no scheduler delays under load.
      //   (b) waitForRadixReleased polls (via rAF) until aria-hidden /
      //       focus-trap are actually gone — no fixed timeout, no
      //       device-dependent race.
      flushSync(() => onOpenChange(false));
      await waitForRadixReleased();

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.order_id,
          name: "Advocate Document Studio",
          description: `Download ${clampedCount} PDF${clampedCount > 1 ? "s" : ""}`,
          prefill: { name: userName || "", email: userEmail || "" },
          // Explicit method allow-list — matches Razorpay's own JS
          // reference for enabling UPI Intent on Android mobile web.
          // Card / Netbanking / Wallet remain enabled exactly as before.
          method: { upi: true, card: true, netbanking: true, wallet: true },
          notes: { display_mode: getDisplayMode() },
          theme: { color: "#8a6e3b" },
          handler: async (rzpRes) => {
            try {
              // 3) Verify server-side
              const vr = await fetch(`${API}/api/payments/verify`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: rzpRes.razorpay_order_id,
                  razorpay_payment_id: rzpRes.razorpay_payment_id,
                  razorpay_signature: rzpRes.razorpay_signature,
                  pdf_count: clampedCount,
                }),
              });
              if (!vr.ok) throw new Error("verify-failed");
              const vj = await vr.json();
              if (!vj.verified || !vj.download_token) throw new Error("verify-not-ok");

              // 4) Consume the token & trigger downloads
              const docKeys = Array.from(selected);
              for (let i = 0; i < docKeys.length; i++) {
                const cr = await fetch(`${API}/api/payments/consume-download`, {
                  method: "POST",
                  credentials: "include",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ download_token: vj.download_token }),
                });
                if (!cr.ok) throw new Error("consume-failed");
              }
              onOpenChange(false);
              toast.success("Payment verified. Preparing downloads…");
              await onDownload(docKeys);
              resolve();
            } catch (err) {
              console.error(err);
              toast.error("Payment was not completed. Please try again.");
              reject(err);
            }
          },
          modal: {
            ondismiss: () => {
              toast.error("Payment was not completed. Please try again.");
              reject(new Error("cancelled"));
            },
          },
        });
        rzp.on("payment.failed", () => {
          toast.error("Payment was not completed. Please try again.");
          reject(new Error("failed"));
        });
        rzp.open();
      });
    } catch (err) {
      // Errors already toasted where relevant
      if (err && err.message !== "cancelled" && err.message !== "failed") {
        console.error(err);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#8a6e3b]" />
            Pay & Download PDFs
          </DialogTitle>
          <DialogDescription>
            Select the documents you would like to download. Pricing:
            <span className="ml-1 font-semibold">₹{PRICE_PER_PDF_RUPEES}</span> per PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {DOC_CATALOG.map((d) => {
            const checked = selected.has(d.key);
            const disabled = !checked && selected.size >= 3;
            return (
              <label
                key={d.key}
                className={`flex items-center justify-between rounded-md border px-3 py-2 cursor-pointer ${
                  checked ? "border-[#8a6e3b] bg-[#8a6e3b]/5" : "border-neutral-200"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`payment-doc-${d.key}`}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={() => toggle(d.key)}
                    data-testid={`payment-doc-${d.key}-checkbox`}
                  />
                  <Label className="cursor-pointer">{d.label}</Label>
                </div>
                <div className="text-sm text-neutral-600">₹{PRICE_PER_PDF_RUPEES}</div>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-sm text-neutral-600">
            {clampedCount === 0 ? "No document selected" : `${clampedCount} PDF${clampedCount > 1 ? "s" : ""}`}
          </div>
          <div className="text-lg font-semibold" data-testid="payment-total">
            ₹{amountRupees.toFixed(0)}
          </div>
        </div>

        {configured === false ? (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Payment gateway is not yet configured. Downloads are temporarily
              unavailable — please try again later.
            </div>
          </div>
        ) : null}

        {isRestrictedUpiIntentContext() ? (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[12.5px] text-amber-900"
            data-testid="civil-upi-standalone-notice"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              You are using the installed app mode. If Google Pay / PhonePe /
              Paytm does not open on tap, open this page in Chrome once — UPI
              apps will then launch normally.
              <button
                type="button"
                onClick={() => {
                  try {
                    window.open(window.location.href, "_blank", "noopener,noreferrer");
                  } catch (_) {
                    window.location.href = window.location.href;
                  }
                }}
                data-testid="civil-upi-open-in-browser"
                className="ml-1 inline-flex items-center gap-0.5 font-semibold text-amber-900 underline"
              >
                Open in Chrome <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="payment-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={startPayment}
            disabled={busy || clampedCount === 0 || configured === false}
            data-testid="payment-pay-btn"
            className="bg-[#8a6e3b] hover:bg-[#6f5830]"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing…
              </>
            ) : (
              <>Pay ₹{amountRupees.toFixed(0)} &amp; Download</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
